import { useCallback, useEffect, useRef, useState } from 'react';

import { USAGE_LIMIT_WARNING_THRESHOLD_SECONDS } from '@pages/Admin/Campaign/ui/constants';
import { useStartStopContext } from '@features/Admin/Campaign/contexts/useStartStop.context';
import { getRemainingCallSeconds } from '@features/Admin/Campaign/utils/campaign-usage.utils';
import { CampaignStatus } from '@pages/Admin/Main/Campaign/ui/CampaignStatusButton';
import { getCampaignStatus } from '@pages/Admin/Main/Campaign/ui/CampaignStatusButton/utils';
import {
  CallScheduleDto,
  CampaignDto,
  apiService,
  useCampaignRunControllerCampaignRunMutation,
  useCampaignStopControllerCampaignStopMutation,
} from '@shared/services/apiService/apiService';
import { getNextAvailableCallTimestamp, isOutOfSchedule } from '@shared/utils/DateUtil';

interface UseCampaignStartStopOptions {
  campaignData?: CampaignDto;
  refetch: () => Promise<unknown>;
}

export const useCampaignStartStop = ({ campaignData, refetch }: UseCampaignStartStopOptions) => {
  const { setStartStopFunction, setCampaignContext } = useStartStopContext();

  const [showScheduleWarningDialogFlag, setShowScheduleWarningDialogFlag] = useState(false);
  const [timeToStart, setTimeToStart] = useState<number>(new Date().getTime());
  const [showUsageLimitWarningDialog, setShowUsageLimitWarningDialog] = useState(false);
  const [showUsageLimitBlockedDialog, setShowUsageLimitBlockedDialog] = useState(false);
  const pendingStartIdRef = useRef<string | null>(null);

  const [useRunCampaign, { isLoading: isRunningLoading }] =
    useCampaignRunControllerCampaignRunMutation();
  const [useStopCampaign, { isLoading: isStoppingLoading }] =
    useCampaignStopControllerCampaignStopMutation();

  const [fetchUsageLimits] = apiService.endpoints.usageLimitControllerUsageLimits.useLazyQuery();

  const closeScheduleDialog = useCallback(() => {
    setShowScheduleWarningDialogFlag(false);
  }, []);

  const proceedWithScheduleCheck = useCallback(
    async (id: string, callSchedule: CallScheduleDto | undefined) => {
      const canRun = !callSchedule || !isOutOfSchedule(callSchedule);
      if (canRun) {
        await useRunCampaign({ id });
      } else {
        const nextAvailableCallTimestamp = getNextAvailableCallTimestamp(
          callSchedule || campaignData?.callSchedule,
        );
        if (nextAvailableCallTimestamp) {
          setTimeToStart(nextAvailableCallTimestamp);
          setShowScheduleWarningDialogFlag(true);
        }
      }
      await refetch();
    },
    [campaignData?.callSchedule, refetch, useRunCampaign],
  );

  const checkUsageLimitsAndStart = useCallback(
    async (id: string, callSchedule: CallScheduleDto | undefined) => {
      try {
        const result = await fetchUsageLimits(undefined, false).unwrap();
        const remaining = getRemainingCallSeconds(result);

        if (remaining <= 0) {
          setShowUsageLimitBlockedDialog(true);
          return;
        }

        if (remaining <= USAGE_LIMIT_WARNING_THRESHOLD_SECONDS) {
          pendingStartIdRef.current = id;
          setShowUsageLimitWarningDialog(true);
          return;
        }
      } catch {
        setShowUsageLimitBlockedDialog(true);
        return;
      }

      await proceedWithScheduleCheck(id, callSchedule);
    },
    [fetchUsageLimits, proceedWithScheduleCheck],
  );

  const handleManagedStartStop = useCallback(
    async (
      id: string,
      callSchedule: CallScheduleDto | undefined,
      campaignStatus: CampaignStatus,
    ) => {
      if (CampaignStatus.NotStarted === campaignStatus) {
        await checkUsageLimitsAndStart(id, callSchedule);
      } else if (CampaignStatus.Dialing === campaignStatus) {
        await useStopCampaign({ id });
        await refetch();
      }
    },
    [checkUsageLimitsAndStart, refetch, useStopCampaign],
  );

  const runCampaign = useCallback(async () => {
    closeScheduleDialog();
    if (campaignData?.id) {
      await useRunCampaign({ id: campaignData.id });
    }
    await refetch();
  }, [campaignData?.id, closeScheduleDialog, refetch, useRunCampaign]);

  const handleUsageLimitWarningConfirm = useCallback(async () => {
    setShowUsageLimitWarningDialog(false);
    const id = pendingStartIdRef.current;
    if (id) {
      pendingStartIdRef.current = null;
      await proceedWithScheduleCheck(id, campaignData?.callSchedule);
    }
  }, [campaignData?.callSchedule, proceedWithScheduleCheck]);

  useEffect(() => {
    setStartStopFunction(handleManagedStartStop);
  }, [handleManagedStartStop, setStartStopFunction]);

  useEffect(() => {
    const campaignStatus = getCampaignStatus(campaignData);
    const nextAvailableCallTimestamp = getNextAvailableCallTimestamp(campaignData?.callSchedule);
    const showTimer =
      nextAvailableCallTimestamp &&
      CampaignStatus.NotStarted !== campaignStatus &&
      isOutOfSchedule(campaignData?.callSchedule!);

    if (showTimer) {
      setTimeToStart(nextAvailableCallTimestamp);
    }

    if (campaignData) {
      setCampaignContext(campaignData);
    }
  }, [campaignData, setCampaignContext]);

  const timeUntilStartCampaignRun = (timeToStart - new Date().getTime()) / 1000;

  return {
    isRunningLoading,
    isStoppingLoading,
    showScheduleWarningDialogFlag,
    setShowScheduleWarningDialogFlag,
    showUsageLimitWarningDialog,
    setShowUsageLimitWarningDialog,
    showUsageLimitBlockedDialog,
    setShowUsageLimitBlockedDialog,
    timeUntilStartCampaignRun,
    closeScheduleDialog,
    runCampaign,
    handleUsageLimitWarningConfirm,
  };
};
